import React, { useState, useEffect, useContext } from "react";
import "../css/NavigationBar.css";
import airportLogo from "../../assets/images/airport-logo.png";
import appLogo from "../../assets/images/app-logo.svg";
import { BellOutlined, LogoutOutlined } from "@ant-design/icons";
import { Avatar, Badge, Dialog } from "@mui/material";
import LogoutConfirmationDialog from "./LogoutConfirmationDialog";
import { useQuery } from "@tanstack/react-query";
import Loader from "./Loader";
import CommercialUserProfileDialog from "./CommercialUserProfileDialog";
import useAirlineAdminApi from "../../services/api/useAirlineAdminApi";
import ROUTE_PATHS from "../../utils/routePaths";
import { useNavigate } from "react-router-dom";
import { useFormData } from "../../helpers/FormDataProvider";
import { StorageContext } from "../../services/StorageProvider";
import { useLogout } from "../../services/LogoutProvider"; // Make sure this path matches your folder structure

const NavigationBar = () => {
  const navigate = useNavigate();
  const { userData } = useFormData();
  const { userProfileData } = useContext(StorageContext);
  const { handleLogout } = useLogout(); // Import the handleLogout function from your provider
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileDialog, setShowProfileDialog] = useState(null);

  // Removed removeDeviceToken from here since the LogoutProvider handles it
  const { fetchNotificationsUnreadCount, fetchAllNotifications } =
    useAirlineAdminApi(userData.isAAUser);
  const [actionCount, setActionCount] = useState(0);

  const {
    data: notificationsData,
    error,
    isLoading,
  } = useQuery({
    queryKey: ["fbo-notifications-list", actionCount],
    queryFn: () =>
      fetchAllNotifications({
        page: 0,
        size: 4,
      }),
    retry: false,
  });

  const { data: notificationsCount } = useQuery({
    queryKey: ["fbo-notifications-count"],
    queryFn: fetchNotificationsUnreadCount,
    retry: false,
    refetchInterval: 30000,
  });

  useEffect(() => {
    setActionCount(actionCount + 1);
  }, [showNotifications]);

  const handleLogoutClick = () => {
    setShowLogoutDialog(true);
  };

  const handleLogoutDialogClose = () => {
    if (isLoggingOut) return;
    setShowLogoutDialog(false);
  };

  const handleLogoutDialogSave = async () => {
    try {
      setIsLoggingOut(true);
      // Let your LogoutProvider handle the heavy lifting (token removal & keycloak logout)
      handleLogout();
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleNotificationClick = () => {
    if (
      window.location.pathname !== ROUTE_PATHS.COMMERCIAL_AIRLINE_NOTIFICATIONS
    ) {
      navigate(ROUTE_PATHS.COMMERCIAL_AIRLINE_NOTIFICATIONS);
    }
    setShowNotifications(false);
  };

  const toggleNotifications = () => {
    setShowNotifications((prev) => !prev);
  };

  return (
    <div className="nav-bar">
      <div className="left-container">
        <img src={airportLogo} className="airport-logo" alt="logo" />
        <img src={appLogo} className="app-logo" alt="logo" />
      </div>
      <div className="center-container">
        <h1>
          <span>Commercial Airline </span>
          <span>Management</span>
        </h1>
      </div>
      <div className="right-container">
        {userProfileData ? (
          <>
            <Avatar
              alt={
                userData.isAAUser
                  ? userProfileData?.data?.id
                  : userProfileData?.id
              }
              className="profile-pic"
              onClick={() => setShowProfileDialog(true)}
            >
              {userData.isAAUser
                ? userProfileData?.data?.firstName.charAt(0)
                : userProfileData?.firstName.charAt(0)}
            </Avatar>
            <div onClick={() => setShowProfileDialog(true)}>{`${userData.isAAUser
              ? userProfileData?.data?.firstName
              : userProfileData?.firstName
              } ${userData.isAAUser
                ? userProfileData?.data?.lastName
                : userProfileData?.lastName
              }`}</div>
          </>
        ) : null}
        <div
          className="notification-bell-wrapper"
          role="button"
          tabIndex={0}
          onClick={toggleNotifications}
        >
          <Badge
            badgeContent={
              notificationsCount?.data > 99 ? "99+" : notificationsCount?.data
            }
            color="primary"
          >
            <BellOutlined className="notification-icon" />
          </Badge>
        </div>
        <Dialog
          open={showNotifications ?? false}
          onClose={() => setShowNotifications(false)}
          slotProps={{
            backdrop: {
              style: {
                backgroundColor: "transparent",
              },
            },
          }}
          PaperProps={{
            style: {
              position: "absolute",
              top: "50px",
              right: "80px",
              width: "300px",
              margin: 0,
            },
          }}
        >
          <div>
            {isLoading ? (
              <Loader />
            ) : error && error.status === 404 ? (
              <div className="notification-screen-no-data">
                No notifications found
              </div>
            ) : notificationsData?.data &&
              notificationsData?.data?.length === 0 ? (
              <div className="notification-screen-no-data">
                No notifications found
              </div>
            ) : (
              <div>
                {notificationsData?.data?.content?.map((item) => (
                  <div
                    key={item?.id}
                    className={`navigation-notification-item ${item?.isRead ? "" : "notification-unread"
                      }`}
                  >
                    <div className="navigation-notification-content">
                      <div className="navigation-notification-message">
                        {item?.notification}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="navigation-notification-footer">
              <span
                className="navigation-notification-view-all"
                onClick={handleNotificationClick}
              >
                View all
              </span>
            </div>
          </div>
        </Dialog>
        <LogoutOutlined
          style={{
            marginLeft: "35px",
            cursor: "pointer",
            fontSize: "20px",
            position: "relative",
            zIndex: 2,
          }}
          onClick={handleLogoutClick}
        />
      </div>
      <LogoutConfirmationDialog
        open={showLogoutDialog}
        onClose={handleLogoutDialogClose}
        onSave={handleLogoutDialogSave}
        isLoading={isLoggingOut}
      />
      <CommercialUserProfileDialog
        open={showProfileDialog}
        onClose={() => setShowProfileDialog(false)}
      />
    </div>
  );
};

export default NavigationBar;